import type { DojoBeat } from '@/types/dojo';
import { UNIT_BEAT_SEQUENCE } from '@/types/dojo';
import { cn } from '@/lib/utils';

interface UnitBeatStepperProps {
  beat: DojoBeat;
  contextLabel?: string;
  onBackToPractice?: () => void;
  onEndRound?: () => void;
  endRoundDisabled?: boolean;
  onContinue?: () => void;
  continueLabel?: string;
  continueDisabled?: boolean;
  className?: string;
}

/**
 * The quiet strip above the round: where you are, and the way out.
 * Progress is shown as a small set of segments rather than a second navigation.
 */
export function UnitBeatStepper({
  beat,
  contextLabel,
  onBackToPractice,
  onEndRound,
  endRoundDisabled = false,
  onContinue,
  continueLabel,
  continueDisabled = false,
  className,
}: UnitBeatStepperProps) {
  const activeIndex = UNIT_BEAT_SEQUENCE.indexOf(beat);
  const position = activeIndex === -1 ? UNIT_BEAT_SEQUENCE.length : activeIndex + 1;

  return (
    <div className={cn('grid min-h-12 grid-cols-2 items-center gap-x-3 gap-y-3 text-xs md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_minmax(0,1fr)]', className)}>
      {onBackToPractice && (
        <button
          type="button"
          onClick={onBackToPractice}
          className="col-start-1 row-start-1 inline-flex min-h-11 items-center justify-self-start rounded-lg border border-how-space-deep/[0.08] bg-white/70 px-4 font-medium text-how-space-deep/55 transition-colors hover:bg-how-space-deep/[0.04] hover:text-how-space-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-space-deep/30"
        >
          Back
        </button>
      )}

      <span
        className="col-span-2 col-start-1 row-start-2 flex min-w-0 flex-col items-center gap-2 md:col-span-1 md:col-start-2 md:row-start-1"
        role="group"
        aria-label={`Round progress: ${position} of ${UNIT_BEAT_SEQUENCE.length}`}
      >
        {contextLabel && (
          <span className="w-full min-w-0 whitespace-normal break-words text-center text-[11px] font-medium uppercase leading-4 tracking-[0.07em] text-how-space-deep/50 md:truncate">
            {contextLabel}
          </span>
        )}
        <span className="flex items-center gap-1" aria-hidden="true">
          {UNIT_BEAT_SEQUENCE.map((sequenceBeat, index) => (
            <span
              key={sequenceBeat}
              className={cn(
                'h-1 w-3.5 rounded-full sm:w-5',
                index <= activeIndex ? 'bg-how-accent-warm' : 'bg-gray-200'
              )}
            />
          ))}
        </span>
      </span>

      <div className="col-start-2 row-start-1 flex items-center justify-self-end gap-3 md:col-start-3">
        {onContinue && (
          <button type="button" onClick={onContinue} disabled={continueDisabled}
            aria-label={continueLabel} title={continueLabel}
            className="min-h-11 whitespace-nowrap px-2 font-medium text-how-space-deep/70 hover:text-how-space-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-space-deep/30 disabled:opacity-50">
            Continue <span aria-hidden="true">→</span>
          </button>
        )}
      {onEndRound && (
        <button
          type="button"
          onClick={onEndRound}
          disabled={endRoundDisabled}
          className="col-start-2 row-start-1 min-h-11 whitespace-nowrap justify-self-end rounded-lg border border-how-space-deep/[0.08] bg-white/70 px-4 font-medium text-how-space-deep/55 transition-colors hover:border-how-accent-warm/20 hover:bg-how-accent-warm/[0.08] hover:text-how-accent-warm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-accent-warm/30 disabled:cursor-not-allowed disabled:opacity-50 md:col-start-3"
        >
          End round
        </button>
      )}
      </div>
    </div>
  );
}

export default UnitBeatStepper;
